import { ListProps, TextField, ReferenceField, FunctionField } from 'react-admin';

import {
  TextInput,
  SelectInput,
  ReferenceInput,
  Grid,
  GridList,
  Forbidden,
} from '@components';
import { checkSuperadmin } from '@providers';
import { StateListActions } from './StateListActions';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';

export const StateList = (props: ListProps) => {
  const isSuperadmin = checkSuperadmin();
  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const exporter = useExporter({
    filename: 'Verqor States',
    props: {
      'id': {
        label: 'ID',
      },
      'name': {
        label: 'Name',
      },
      'country.name': {
        label: 'Country',
      },
      'stateCode': {
        label: 'State Code',
      },
      'latitude': {
        label: 'Latitude',
      },
      'longitude': {
        label: 'Longitude',
      },
      'environmental': {
        label: 'Environmental',
        transform: 'percentAndEmpty',
      },
      'economics': {
        label: 'Economics',
        transform: 'percentAndEmpty',
      },
      'governance': {
        label: 'Governance',
        transform: 'percentAndEmpty',
      },
      'financially': {
        label: 'Financially',
        transform: 'percentAndEmpty',
      },
      'socially': {
        label: 'Socially',
        transform: 'percentAndEmpty',
      },
      'safety': {
        label: 'Safety',
        transform: 'percentAndEmpty',
      },
      'climateResilience': {
        label: 'Climate Resilience',
        transform: 'percentAndEmpty',
      },
      'crT': {
        label: 'CR (T)',
        transform: 'percentAndEmpty',
      },
      'possiblePhenomena': {
        label: 'Possible Phenomena',
      },
      'ponderation': {
        label: 'Ponderation',
        transform: 'percentAndEmpty',
      },
      'grade': {
        label: 'Grade',
      },
    },
  });

  const addPercent = (value: number | null): string => {
    return value === null ? '' : `${value}%`;
  };

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <GridList {...props}
      filters={Filters}
      sort={{ field: 'name', order: 'ASC' }}
      exporter={exporter}
      editComponent={rbacEdit ? <StateListActions /> : undefined}
    >
      <Grid rowClick="edit" defaultColumns={'id name countryId country.name stateCode'.split(' ')}>
        <TextField source="id" label="ID" />
        <TextField source="name" label="Name" />
        {isSuperadmin ? (
          <ReferenceField source="countryId" reference="country" label="Country">
            <TextField source="name" />
          </ReferenceField>
        ) : (
          <TextField source="country.name" label="Country" />
        )}
        <TextField source="stateCode" label="State Code" />
        <TextField source="latitude" label="Latitude" />
        <TextField source="longitude" label="Longitude" />
        <FunctionField source="environmental" label="Environmental" render={r => addPercent(r.environmental)}/>
        <FunctionField source="economics" label="Economics" render={r => addPercent(r.economics)}/>
        <FunctionField source="governance" label="Governance" render={r => addPercent(r.governance)}/>
        <FunctionField source="financially" label="Finance" render={r => addPercent(r.financially)}/>
        <FunctionField source="socially" label="Socially" render={r => addPercent(r.socially)}/>
        <FunctionField source="safety" label="Safety" render={r => addPercent(r.safety)}/>
        <FunctionField
          source="climateResilience"
          label="Climate Resilience"
          render={r => addPercent(r.climateResilience)}/>
        <FunctionField source="crT" label="CR (T)" render={r => addPercent(r.crT)}/>
        <TextField source="possiblePhenomena" label="Possible Phenomena" />
        <FunctionField source="ponderation" label="Ponderation" render={r => addPercent(r.ponderation)}/>
        <TextField source="grade" label="Grade" />
      </Grid>
    </GridList>
  );
};

const Filters = [
  <TextInput source="q" key="q" variant="outlined" label="Search" alwaysOn />,
  <ReferenceInput
    source="countryId"
    key="countryId"
    reference="country"
    label="Country"
    allowEmpty
    alwaysOn
    perPage={1000}
    sort={{ field: 'name', order: 'ASC' }}
  >
    <SelectInput optionText="name" />
  </ReferenceInput>,
];
